import React from "react";

type ButtonVariant =
  | "primary"
  | "dark"
  | "btn-red"
  | "outline"
  | "ghost"
  | "link";

type ButtonSize = "sm" | "md" | "lg";

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  isDisabled?: boolean;
  /** When true, button is disabled and shows a busy cursor (blocks double-submit). */
  isLoading?: boolean;
  iconSrc?: string;
  showBefore?: boolean;
  showAfter?: boolean;
  iconClassName?: string;
};

const baseClasses =
  "inline-flex items-center justify-center gap-1 font-medium transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-blue rounded-[8px]";

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-9 px-3 text-sm",
  md: "h-10 px-4 text-md",
  lg: "h-[50px] px-5 text-lg",
};

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "border border-bright-green bg-bright-green text-dark-blue hover:brightness-95 font-semibold",
  dark: "border border-dark-blue bg-dark-blue text-white hover:brightness-110",
  "btn-red": "border border-btn-red bg-btn-red text-white hover:brightness-95",
  outline:
    "border border-black bg-white text-dark-black hover:bg-primary font-normal",
  ghost: "border border-transparent bg-transparent text-dark-grey hover:bg-primary",
  link: "border border-transparent bg-transparent p-0 text-brand-blue underline-offset-4 hover:underline",
};

export default function Button({
  variant = "primary",
  size = "md",
  fullWidth = false,
  isDisabled = false,
  isLoading = false,
  iconSrc,
  showBefore = false,
  showAfter = false,
  iconClassName = "",
  className = "",
  type = "button",
  disabled,
  children,
  onClick,
  ...props
}: ButtonProps) {
  const disabledState = isDisabled || isLoading || Boolean(disabled);

  const disabledClasses =
    variant === "outline"
      ? "disabled:bg-light-grey disabled:border-light-grey disabled:text-dark-grey disabled:hover:bg-light-grey"
      : "";

  const composedClassName = [
    baseClasses,
    sizeClasses[size],
    variantClasses[variant],
    disabledClasses,
    fullWidth ? "w-full" : "",
    isLoading ? "pointer-events-none" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const iconEl = iconSrc ? (
    <img src={iconSrc} alt="" aria-hidden="true" className={["h-5 w-5", iconClassName].filter(Boolean).join(" ")} />
  ) : null;

  return (
    <button
      type={type}
      className={composedClassName}
      disabled={disabledState}
      aria-busy={isLoading || undefined}
      onClick={(e) => {
        if (disabledState) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onClick?.(e);
      }}
      {...props}
    >
      {showBefore ? iconEl : null}
      <span>{children}</span>
      {showAfter ? iconEl : null}
    </button>
  );
}

